import type { ReactElement } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { canUseCardWallet } from "@/app/auth/canUseCardWallet";
import { RequireAccess } from "@/app/auth/RequireAccess";
import { findEntryByTo, isEntryRoutable } from "@/app/auth/resolveNavAccess";
import { useNavContext } from "@/app/auth/useNavContext";
import { useAuth } from "@/modules/auth/hooks/useAuth";

import { usePocketsVisibility } from "./hooks/usePocketsVisibility";
import { BrandingPage } from "./pages/BrandingPage";
import { CardDetailPage } from "./pages/CardDetailPage";
import { CardNotificationsPage } from "./pages/CardNotificationsPage";
import { CardsPage } from "./pages/CardsPage";
import { IdentitiesPage } from "./pages/IdentitiesPage";
import { IdentityDetailPage } from "./pages/IdentityDetailPage";
import { OrganizationsPage } from "./pages/OrganizationsPage";
import { PocketDetailPage } from "./pages/PocketDetailPage";
import { PocketsPage } from "./pages/PocketsPage";
import { WalletMovementsPage } from "./pages/WalletMovementsPage";

/**
 * The surfaces under `cards/*` do not share a requirement — holders read the identities module, pockets read
 * the wallets module, the statement reads the wallets module too. The parent route opens when ANY of them is
 * reachable, so each sub-route carries its own `RequireAccess`; otherwise entering through one surface would
 * grant the rest, which is the menu-vs-route divergence this design exists to prevent.
 */

/**
 * Landing preference for the module index, most specific first. Pockets is excluded on purpose: it sits
 * behind a feature flag, and picking it as the default would bounce through `guardPocket`.
 */
const INTERNAL_LANDING_ORDER = ["/app/cards/organizations", "/app/cards/cards", "/app/cards/identities"];
const MEMBER_LANDING_ORDER = ["/app/cards/cards", "/app/cards/identities"];

export const CardsNavigation = () => {
  const { isInternalUser, user } = useAuth();
  const hasCardWallet = canUseCardWallet(user);
  const navContext = useNavContext();
  const { visible: pocketsVisible, isLoading: pocketsVisibilityLoading } = usePocketsVisibility();

  const landingOrder = isInternalUser ? INTERNAL_LANDING_ORDER : MEMBER_LANDING_ORDER;
  // Resolve to the first surface this user can actually open, checked against its OWN entry — a group
  // check would answer "yes" for every member of `cards/*` and send a holders-only user to Cards, which
  // then denies them. The previous fixed target had the same problem once sub-routes became guarded.
  const defaultRoute =
    (navContext &&
      landingOrder.find((to) => {
        const entry = findEntryByTo(to);
        return entry ? isEntryRoutable(entry, navContext) : false;
      })) ??
    "/app/cards/cards";

  // Direct-URL guard: while visibility resolves render nothing, then either
  // the page (funded pockets) or a redirect away (feature gated off).
  const guardPocket = (element: ReactElement): ReactElement => {
    if (pocketsVisible) return element;
    if (pocketsVisibilityLoading) return <></>;
    return <Navigate to="/app/cards/cards" replace />;
  };

  return (
    <Routes>
      <Route index element={<Navigate to={defaultRoute} replace />} />
      <Route
        path="identities"
        element={
          <RequireAccess to="/app/cards/identities">
            <IdentitiesPage />
          </RequireAccess>
        }
      />
      <Route
        path="identities/:id"
        element={
          <RequireAccess to="/app/cards/identities">
            <IdentityDetailPage />
          </RequireAccess>
        }
      />
      <Route
        path="organizations"
        element={
          <RequireAccess to="/app/cards/organizations">
            <OrganizationsPage />
          </RequireAccess>
        }
      />
      <Route
        path="cards"
        element={
          <RequireAccess to="/app/cards/cards">
            <CardsPage />
          </RequireAccess>
        }
      />
      <Route
        path="cards/:id"
        element={
          <RequireAccess to="/app/cards/cards">
            <CardDetailPage />
          </RequireAccess>
        }
      />
      <Route
        path="pockets"
        element={<RequireAccess to="/app/cards/pockets">{guardPocket(<PocketsPage />)}</RequireAccess>}
      />
      <Route
        path="pockets/:id"
        element={<RequireAccess to="/app/cards/pockets">{guardPocket(<PocketDetailPage />)}</RequireAccess>}
      />
      <Route
        path="movements"
        element={
          <RequireAccess to="/app/cards/movements">
            {/* The card wallet is opt-in: without it there is no statement to read. */}
            {hasCardWallet ? <WalletMovementsPage /> : <Navigate to="/app/cards/cards" replace />}
          </RequireAccess>
        }
      />
      <Route
        path="branding"
        element={
          <RequireAccess to="/app/cards/branding">
            <BrandingPage />
          </RequireAccess>
        }
      />
      <Route
        path="notifications"
        element={
          <RequireAccess to="/app/cards/notifications">
            <CardNotificationsPage />
          </RequireAccess>
        }
      />

      <Route path="list" element={<Navigate to="/app/cards/cards" />} />
      <Route path="movimientos" element={<Navigate to="/app/cards/movements" replace />} />
    </Routes>
  );
};
